import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FileSliders, Plus, Eye, Download, Trash2,
  FileText, CheckCircle, Archive, Calendar
} from 'lucide-react';
import api, { downloadBlob } from '../services/api';
import { getDateLocale } from '../utils/i18nHelpers';
import './PresentationList.css';

const STATUS_CSS = {
  draft:      'pres-status-draft',
  generated:  'pres-status-generated',
  downloaded: 'pres-status-downloaded',
  archived:   'pres-status-archived',
};

const TYPE_CSS = {
  period_results: 'pres-type-period',
  achievements:   'pres-type-achievements',
  analytics:      'pres-type-analytics',
  meeting:        'pres-type-meeting',
};

function PresentationList() {
  const { t } = useTranslation(['presentations', 'common']);
  const navigate = useNavigate();
  const [presentations, setPresentations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, generated: 0, downloaded: 0, templates_count: 0 });

  const [filters, setFilters] = useState({
    presentation_type: '',
    status: '',
    year: '',
  });

  const currentYear = new Date().getFullYear();
  const years = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchPresentations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      await fetchPresentations();
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const fetchPresentations = async () => {
    try {
      const params = {};
      if (filters.presentation_type) params.presentation_type = filters.presentation_type;
      if (filters.status) params.status = filters.status;
      if (filters.year) params.year = filters.year;

      const [presRes, statsRes] = await Promise.all([
        api.getPresentations(params),
        api.getPresentationStatistics(),
      ]);
      setPresentations(presRes.data.results || presRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching presentations:', error);
    }
  };

  const handleDownload = async (pres) => {
    try {
      const r = await api.downloadPresentation(pres.id);
      downloadBlob(r, `${pres.title}.pptx`);
      fetchPresentations();
    } catch (error) {
      alert(t('presentations:downloadError'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('presentations:messages.deleteConfirm'))) return;
    try {
      await api.deletePresentation(id);
      fetchPresentations();
    } catch (error) {
      console.error('Error deleting presentation:', error);
    }
  };

  if (loading) {
    return (
      <div className="pres-list-page">
        <div className="loading-spinner">{t('common:table.loading')}</div>
      </div>
    );
  }

  return (
    <div className="pres-list-page">
      {/* Header */}
      <div className="pres-list-header">
        <h1>
          <FileSliders size={24} />
          {t('presentations:title')}
        </h1>
        <div className="header-actions">
          <button className="btn-primary" onClick={() => navigate('/presentations/create')}>
            <Plus size={18} />
            {t('presentations:createPresentation')}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="pres-stats">
        <div className="pres-stat-card blue">
          <div className="stat-icon"><FileSliders size={24} /></div>
          <div>
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">{t('presentations:stats.total')}</div>
          </div>
        </div>
        <div className="pres-stat-card green">
          <div className="stat-icon"><CheckCircle size={24} /></div>
          <div>
            <div className="stat-value">{stats.generated}</div>
            <div className="stat-label">{t('presentations:stats.generated')}</div>
          </div>
        </div>
        <div className="pres-stat-card orange">
          <div className="stat-icon"><Download size={24} /></div>
          <div>
            <div className="stat-value">{stats.downloaded}</div>
            <div className="stat-label">{t('presentations:stats.downloaded')}</div>
          </div>
        </div>
        <div className="pres-stat-card purple">
          <div className="stat-icon"><FileText size={24} /></div>
          <div>
            <div className="stat-value">{stats.templates_count}</div>
            <div className="stat-label">{t('presentations:stats.templates')}</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-card">
        <div className="filters-row">
          <div className="filter-group">
            <label>{t('presentations:type')}</label>
            <select
              className="filter-control"
              value={filters.presentation_type}
              onChange={e => setFilters({ ...filters, presentation_type: e.target.value })}
            >
              <option value="">{t('presentations:filters.allTypes')}</option>
              <option value="period_results">{t('presentations:types.period_results')}</option>
              <option value="achievements">{t('presentations:types.achievements')}</option>
              <option value="analytics">{t('presentations:types.analytics')}</option>
              <option value="meeting">{t('presentations:types.meeting')}</option>
            </select>
          </div>
          <div className="filter-group">
            <label>{t('common:table.status')}</label>
            <select
              className="filter-control"
              value={filters.status}
              onChange={e => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="">{t('presentations:filters.allStatuses')}</option>
              <option value="draft">{t('presentations:statuses.draft')}</option>
              <option value="generated">{t('presentations:statuses.generated')}</option>
              <option value="downloaded">{t('presentations:statuses.downloaded')}</option>
              <option value="archived">{t('presentations:statuses.archived')}</option>
            </select>
          </div>
          <div className="filter-group">
            <label>{t('presentations:year')}</label>
            <select
              className="filter-control"
              value={filters.year}
              onChange={e => setFilters({ ...filters, year: e.target.value })}
            >
              <option value="">{t('presentations:filters.allYears')}</option>
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="pres-table-card">
        <h2>
          <FileSliders size={20} />
          {t('presentations:title')} ({presentations.length})
        </h2>

        {presentations.length === 0 ? (
          <div className="empty-state">
            <Archive size={48} />
            <h3>{t('presentations:empty.noPresentations')}</h3>
            <p>{t('presentations:empty.createFirst')}</p>
          </div>
        ) : (
          <table className="pres-table">
            <thead>
              <tr>
                <th>{t('common:table.name')}</th>
                <th>{t('presentations:type')}</th>
                <th>{t('presentations:template')}</th>
                <th>{t('common:table.department')}</th>
                <th>{t('presentations:year')}</th>
                <th>{t('presentations:slidesCount')}</th>
                <th>{t('common:table.status')}</th>
                <th>{t('common:table.date')}</th>
                <th>{t('common:table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {presentations.map(pres => {
                const statusCss = STATUS_CSS[pres.status] || STATUS_CSS.draft;
                const statusLabel = t(`presentations:statuses.${pres.status}`, { defaultValue: pres.status });
                const typeCss = TYPE_CSS[pres.presentation_type] || '';
                const typeLabel = t(`presentations:types.${pres.presentation_type}`, { defaultValue: pres.presentation_type });

                return (
                  <tr key={pres.id}>
                    <td>
                      <span
                        className="pres-title-link"
                        onClick={() => navigate(`/presentations/${pres.id}`)}
                      >
                        {pres.title}
                      </span>
                    </td>
                    <td>
                      <span className={`pres-type-badge ${typeCss}`}>
                        {typeLabel}
                      </span>
                    </td>
                    <td>{pres.template_name || '—'}</td>
                    <td>{pres.department_name || t('presentations:form.allOrg')}</td>
                    <td>{pres.year}</td>
                    <td>{pres.slides_count}</td>
                    <td>
                      <span className={`pres-status-badge ${statusCss}`}>
                        {statusLabel}
                      </span>
                    </td>
                    <td>
                      {pres.generated_at
                        ? new Date(pres.generated_at).toLocaleDateString(getDateLocale())
                        : new Date(pres.created_at).toLocaleDateString(getDateLocale())
                      }
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon"
                          title={t('presentations:preview')}
                          onClick={() => navigate(`/presentations/${pres.id}`)}
                        >
                          <Eye size={14} />
                        </button>
                        {pres.slides_count > 0 && (
                          <button
                            className="btn-icon btn-download"
                            title={t('presentations:download')}
                            onClick={() => handleDownload(pres)}
                          >
                            <Download size={14} />
                          </button>
                        )}
                        <button
                          className="btn-icon btn-danger"
                          title={t('common:buttons.delete')}
                          onClick={() => handleDelete(pres.id)}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default PresentationList;
